import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import AuthFrame from '@/components/auth/AuthFrame';
import { useToast } from '@/components/ToastProvider';
import useAuth from '@/hooks/useAuth';
import {
  forgotPassword,
  login,
  resetPassword,
  saveIdToken,
  toAuthApiError,
} from '@/services/authApi';
import { URL } from '@/types';

type ResetForm = { email: string; code: string; password: string; passwordConfirm: string };
/**
 * ForgotPassword から渡される。
 * router state（history.state）はリロード・戻る/進むでも残る（ブラウザがセッション履歴として保持するため）。
 * 直接 URL を開いた場合や新しいタブでは無い
 */
type ResetState = { email?: string; sent?: boolean } | null;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state } = useLocation() as { state: ResetState };
  const { validationRules, errorMessage } = useAuth();
  const knownEmail = state?.email;
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const { control, handleSubmit, getValues, trigger, watch } = useForm<ResetForm>({
    defaultValues: { email: knownEmail ?? '', code: '', password: '', passwordConfirm: '' },
    mode: 'onChange',
  });
  const password = watch('password');

  const onSubmit = async ({ email, code, password }: ResetForm) => {
    setError(undefined);
    setSubmitting(true);
    try {
      await resetPassword(email, code, password);
    } catch (e) {
      setError(errorMessage(toAuthApiError(e).code));
      setSubmitting(false);
      return;
    }

    // 自動ログイン（spec §3.3）。失敗したらログイン画面へ
    try {
      const { id_token } = await login(email, password);
      saveIdToken(id_token);
      toast({ message: t('pages:auth:reset_password:done') });
      navigate('/', { replace: true });
    } catch {
      toast({ message: t('pages:auth:reset_password:done_login') });
      navigate(URL.LOGIN, { replace: true, state: { email } });
    }
  };

  const onResendClick = async () => {
    // state 無し画面ではメール欄の入力チェックを通してから再送する
    if (resending || !(await trigger('email'))) {
      return;
    }
    setResending(true);
    setError(undefined);
    try {
      await forgotPassword(getValues('email'));
      toast({ message: t('pages:auth:messages:code_resent') });
    } catch (e) {
      setError(errorMessage(toAuthApiError(e).code));
    } finally {
      setResending(false);
    }
  };

  // t() の戻り型 DefaultTFuncReturn（string | null）は AuthFrame の info（string | undefined）と衝突するため、
  // 変数に一度受けて渡す（既存キーは必ず文字列を返すので String() での挙動変化は無い。useAuth.ts と同じ流儀）
  const info = state?.sent ? String(t('pages:auth:forgot_password:sent')) : undefined;

  return (
    <AuthFrame title={t('pages:auth:reset_password:title')} info={info} error={error}>
      <Typography sx={{ mb: 3 }}>
        {knownEmail
          ? t('pages:auth:reset_password:lead', { email: knownEmail })
          : t('pages:auth:reset_password:lead_no_email')}
      </Typography>
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} sx={{ mb: 4 }}>
          {!knownEmail && (
            <Controller
              name="email"
              control={control}
              rules={validationRules.email}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="email"
                  autoComplete="email"
                  label={t('pages:auth:labels:email')}
                  error={!!fieldState.error?.message}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
          <Controller
            name="code"
            control={control}
            rules={validationRules.code}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                inputProps={{ inputMode: 'numeric' }}
                autoComplete="one-time-code"
                label={t('pages:auth:labels:code')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={validationRules.password}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="password"
                autoComplete="new-password"
                label={t('pages:auth:labels:new_password')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message ?? t('pages:auth:messages:password_rule')}
              />
            )}
          />
          <Controller
            name="passwordConfirm"
            control={control}
            rules={validationRules.passwordConfirm(password)}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                type="password"
                autoComplete="new-password"
                label={t('pages:auth:labels:new_password_confirm')}
                error={!!fieldState.error?.message}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button type="submit" color="primary" variant="contained" disabled={submitting}>
            {t('pages:auth:reset_password:submit')}
          </Button>
        </Box>
        <Stack alignItems="center">
          <Button type="button" variant="text" onClick={onResendClick} disabled={resending}>
            {t('pages:auth:reset_password:resend')}
          </Button>
        </Stack>
      </Box>
    </AuthFrame>
  );
}
